import { useEffect, useState } from 'react';
import { ROLE_ADMIN, ROLE_FARMER, ROLE_SUPER_ADMIN, ROLE_TECHNICIAN } from 'utils/const';
import useAuth from 'providers/Auth/useAuth';
import { ICulture } from 'components/generics/Interface/ICulture';
import { IGroundType } from 'components/generics/Interface/Api/Response/GroundType/IGroundType';

export type selectAllType = {
  [stateIsOrganic: string]: {
    rows: boolean;
    columns: boolean;
  };
};

export type filtersType = {
  [stateIsOrganic: string]: {
    rows?: Record<number, boolean>;
    columns?: Record<number, boolean>;
  };
};

type filteredRotationsType = {
  role: number;
  maskedRotationsByLevel: maskedRotationsByLevelType[];
};

type maskedRotationsByLevelType = {
  structure?: number;
  sector?: number;
  exploitation?: number;
  maskedRows: {
    [stateIsOrganic: string]: filtersType;
  };
};

const useFilteredRotationsLocalStorage = (
  isOrganic: boolean,
  stateIsOrganic: string,
  ownerId: number,
  ownerLevel: string,
  cultures: ICulture[],
  from: string,
  groundTypes?: IGroundType[]
) => {
  const { user } = useAuth();
  const [toggleFilter, setToggleFilter] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<filtersType>({});
  const filterNameForLocalStorage =
    from === 'groundTypeEffect' ? 'filteredGroundTypeEffects' : 'filteredRotationEffects';

  const isAllSelected = (
    type: keyof { rows?: Record<number, boolean>; columns?: Record<number, boolean> }
  ): boolean => {
    if (selectedFilters?.[stateIsOrganic]?.[type]) {
      const filters = selectedFilters?.[stateIsOrganic]?.[type];
      if (filters && typeof filters === 'object') {
        return Object.values(filters).every((value) => Boolean(value));
      }
    }
    return true;
  };

  const [selectAllFilters, setSelectAllFilters] = useState<selectAllType>({
    [stateIsOrganic]: {
      rows: isAllSelected('rows'),
      columns: isAllSelected('columns'),
    },
  });

  const isActiveFilters = (): boolean => {
    if (selectedFilters[stateIsOrganic] && Object.keys(selectedFilters).length > 0) {
      let isActive = false;
      Object.values(selectedFilters[stateIsOrganic]).forEach((filter) => {
        if (Object.values(filter).some((value) => value === false)) {
          isActive = true;
        }
      });
      return isActive;
    }
    return false;
  };

  const areAllValuesTrue = (state: string): boolean => {
    if (
      selectedFilters[state]?.rows &&
      Object.keys(selectedFilters[state].rows).length > 0 &&
      selectedFilters[state]?.columns &&
      Object.keys(selectedFilters[state].columns).length > 0
    ) {
      const allRowsTrue = Object.values(selectedFilters[state].rows).every(
        (value) => value === true
      );
      const allColumnsTrue = Object.values(selectedFilters[state].columns).every(
        (value) => value === true
      );
      return allRowsTrue && allColumnsTrue;
    }
    return true;
  };

  const isConventionalAllTrue = areAllValuesTrue('conventional');
  const isOrganicAllTrue = areAllValuesTrue('organic');

  const checkFiltersEquality = (newFilters: filtersType, oldFilters: filtersType): boolean => {
    for (const filterKey in newFilters) {
      for (const key in newFilters[filterKey as keyof filtersType] as Record<number, boolean>) {
        if (
          (newFilters[filterKey] as Record<number, boolean>)?.[key] !==
          (oldFilters[filterKey] as Record<number, boolean>)?.[key]
        ) {
          return false;
        }
      }
    }
    return true;
  };

  let filteredRotationEffects: filteredRotationsType | null = null;
  const filteredRotationEffectsFromLS: string | null =
    localStorage.getItem(filterNameForLocalStorage);
  if (typeof filteredRotationEffectsFromLS === 'string')
    filteredRotationEffects = JSON.parse(filteredRotationEffectsFromLS);

  const setFilteredRotationsLocalStorage = (object: filteredRotationsType) =>
    localStorage.setItem(filterNameForLocalStorage, JSON.stringify(object));

  let userMaskedRotationsIndex: number = -1;

  if (filteredRotationEffects) {
    switch (ownerLevel) {
      case 'cooperatives':
        userMaskedRotationsIndex = filteredRotationEffects.maskedRotationsByLevel.findIndex(
          (obj: maskedRotationsByLevelType) => obj?.structure === ownerId
        );
        break;
      case 'sectors':
        userMaskedRotationsIndex = filteredRotationEffects.maskedRotationsByLevel.findIndex(
          (obj: maskedRotationsByLevelType) => obj?.sector === ownerId
        );
        break;
      case 'exploitations':
        userMaskedRotationsIndex = filteredRotationEffects.maskedRotationsByLevel.findIndex(
          (obj: maskedRotationsByLevelType) => obj?.exploitation === ownerId
        );
        break;
    }
  }

  const updateMaskedRotationsObject: maskedRotationsByLevelType = {
    ...(ownerLevel === 'cooperatives' && {
      structure: ownerId,
    }),
    ...(ownerLevel === 'sectors' && { sector: ownerId }),
    ...(ownerLevel === 'exploitations' && { exploitation: ownerId }),
    maskedRows: selectedFilters,
  };

  const updateStateOrganicFiltersFromLS = (initialSelectedFilters: filtersType) => {
    if (userMaskedRotationsIndex === -1) {
      setSelectedFilters(initialSelectedFilters);
    } else if (userMaskedRotationsIndex !== -1 && filteredRotationEffects) {
      const maskedRows =
        filteredRotationEffects.maskedRotationsByLevel[userMaskedRotationsIndex].maskedRows;
      if (
        maskedRows[stateIsOrganic] === undefined ||
        Object.keys(maskedRows[stateIsOrganic]).length === 0
      ) {
        maskedRows[stateIsOrganic] = initialSelectedFilters[stateIsOrganic];
      }
      setSelectedFilters(maskedRows);

      setToggleFilter(true);
    }
  };

  const initializeFilters = (
    cultures: ICulture[],
    groundTypes: IGroundType[] | undefined,
    from: string,
    stateIsOrganic: string
  ) => {
    const initialSelectedFilters: filtersType = {
      ...selectedFilters,
    };

    initialSelectedFilters[stateIsOrganic] = {
      rows: {},
      columns: {},
    };

    if (from === 'groundTypeEffect') {
      cultures?.map((culture) => {
        groundTypes?.map((groundType) => {
          if (
            initialSelectedFilters[stateIsOrganic]?.rows &&
            initialSelectedFilters[stateIsOrganic]?.columns
          ) {
            initialSelectedFilters[stateIsOrganic].rows[culture.id] = true;
            initialSelectedFilters[stateIsOrganic].columns[groundType.id] = true;

            updateStateOrganicFiltersFromLS(initialSelectedFilters);
          }
        });
      });
    } else {
      cultures?.map((culture) => {
        if (
          initialSelectedFilters[stateIsOrganic]?.rows &&
          initialSelectedFilters[stateIsOrganic]?.columns
        ) {
          initialSelectedFilters[stateIsOrganic].rows[culture.id] = true;
          initialSelectedFilters[stateIsOrganic].columns[culture.id] = true;

          updateStateOrganicFiltersFromLS(initialSelectedFilters);
        }
      });
    }
  };

  const cultureStateOrganic = isOrganic
    ? cultures?.filter((culture) => culture.isOrganic === true)
    : cultures?.filter((culture) => culture.isOrganic === false);

  useEffect(() => {
    if (cultures && cultures.length > 0 && cultureStateOrganic) {
      initializeFilters(cultureStateOrganic, groundTypes, from, stateIsOrganic);
      setSelectAllFilters((prev) => ({
        ...prev,
        [stateIsOrganic]: { rows: true, columns: true },
      }));
    }
  }, [cultures, groundTypes]);

  useEffect(() => {
    if (
      filteredRotationEffects &&
      selectedFilters &&
      Object.keys(selectedFilters).length > 0 &&
      userMaskedRotationsIndex !== -1
    ) {
      if (isConventionalAllTrue && isOrganicAllTrue) {
        if (filteredRotationEffects?.maskedRotationsByLevel.length === 1) {
          localStorage.removeItem(filterNameForLocalStorage);
        } else {
          filteredRotationEffects.maskedRotationsByLevel.splice(userMaskedRotationsIndex, 1);
          setFilteredRotationsLocalStorage(filteredRotationEffects);
        }
      } else {
        filteredRotationEffects.maskedRotationsByLevel[userMaskedRotationsIndex].maskedRows =
          selectedFilters;
        setFilteredRotationsLocalStorage(filteredRotationEffects);
      }
    }
  }, [selectedFilters, filteredRotationEffects, stateIsOrganic]);

  if (filteredRotationEffects) {
    if (
      userMaskedRotationsIndex !== -1 &&
      !checkFiltersEquality(
        selectedFilters,
        filteredRotationEffects?.maskedRotationsByLevel?.[userMaskedRotationsIndex]?.maskedRows
      )
    ) {
      filteredRotationEffects.maskedRotationsByLevel[userMaskedRotationsIndex].maskedRows =
        selectedFilters;
    } else if (userMaskedRotationsIndex === -1 && isActiveFilters()) {
      filteredRotationEffects.maskedRotationsByLevel.push(updateMaskedRotationsObject);
    }
    setFilteredRotationsLocalStorage(filteredRotationEffects);
  } else if (userMaskedRotationsIndex === -1 && isActiveFilters()) {
    let maskedRotationsByUser: filteredRotationsType = { role: 0, maskedRotationsByLevel: [] };
    switch (user?.role) {
      case 'SUPER_ADMIN':
        maskedRotationsByUser.role = ROLE_SUPER_ADMIN;
        break;
      case 'ADMIN':
        maskedRotationsByUser.role = ROLE_ADMIN;
        break;
      case 'TECHNICIAN':
        maskedRotationsByUser.role = ROLE_TECHNICIAN;
        break;
      case 'FARMER':
        maskedRotationsByUser.role = ROLE_FARMER;
        break;
    }
    maskedRotationsByUser.maskedRotationsByLevel = [updateMaskedRotationsObject];
    setFilteredRotationsLocalStorage(maskedRotationsByUser);
  }

  return {
    toggleFilter,
    setToggleFilter,
    selectedFilters,
    setSelectedFilters,
    selectAllFilters,
    setSelectAllFilters,
  };
};

export default useFilteredRotationsLocalStorage;
